<template>
    <div class="reservations-card">
        <h2 class="reservations-card__title">Reserveringen</h2>

        <div class="reservations-table">
            <div class="reservations-table__header">
                <span>Type voertuig</span>
                <div class="reservations-table__header-cols">
                    <span>Huurperiode</span>
                    <span>Aantal dagen</span>
                </div>
            </div>

            <template v-for="(reservation, index) in reservations">
                <div
                    class="reservations-row"
                    :class="{ 'reservations-row--active': reservation.active }"
                >
                    <div class="reservations-row__vehicle">
                        <div class="reservations-row__image-wrap">
                            <img :src="reservation.vehicle_image" :alt="reservation.vehicle_name">
                        </div>
                        <div class="reservations-row__info">
                            <span class="reservations-row__name">{{ reservation.vehicle_name }}</span>
                            <span class="reservations-row__subtitle">{{ reservation.vehicle_subtitle }}</span>
                        </div>
                    </div>

                    <div class="reservations-row__meta">
                        <span class="reservations-row__period">{{ reservation.period }}</span>
                        <span class="reservations-row__days">{{ reservation.days }} dagen</span>
                        <a :href="reservation.repeat_url" class="reservations-row__repeat">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/>
                            </svg>
                            Herhaal reservering
                        </a>
                    </div>
                </div>

                <div v-if="index < reservations.length - 1" class="reservations-table__divider"></div>
            </template>

            <div class="reservations-table__divider"></div>
        </div>

        <div class="reservations-card__footer">
            <a :href="allReservationsUrl" class="reservations-card__link">
                Bekijk alle reserveringen
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
            </a>
            <a :href="newReservationUrl" class="reservations-card__cta">
                Maak een nieuwe reservering
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
            </a>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CustomerReservations',

    props: {
        reservations: {
            type: Array,
            default: () => []
        },
        allReservationsUrl: {
            type: String,
            default: '/dashboard/reserveringen'
        },
        newReservationUrl: {
            type: String,
            default: '/aanbod'
        }
    }
}
</script>

<style scoped>
.reservations-card {
    background: #ffffff;
    border-radius: 5px;
    box-shadow: 0 8px 5px rgba(0, 125, 147, 0.13);
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
}

@media (min-width: 768px) {
    .reservations-card {
        padding: 24px;
    }
}

.reservations-card__title {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 28px;
    color: #00abc8;
    margin: 0;
}

/* Table */
.reservations-table {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.reservations-table__header {
    display: none;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid #cdd6d7;
    border-bottom: 1px solid #cdd6d7;
    padding: 8px 4px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    color: rgba(3, 50, 57, 0.5);
    text-transform: uppercase;
}

@media (min-width: 992px) {
    .reservations-table__header {
        display: flex;
    }
}

.reservations-table__header-cols {
    display: flex;
    gap: 32px;
    padding-right: calc(6px + 1em + 108px); /* align with meta columns, accounting for repeat button */
}

.reservations-table__divider {
    height: 1px;
    background: #cdd6d7;
    width: 100%;
}

/* Row */
.reservations-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px 4px;
    width: 100%;
    box-sizing: border-box;
    gap: 16px;
}

@media (min-width: 992px) {
    .reservations-row {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
}

.reservations-row:hover {
    background: rgba(237, 112, 42, 0.1);
    border-radius: 5px;
}

.reservations-row:hover .reservations-row__name {
    color: #ed702a;
    text-decoration: underline;
}

.reservations-row:hover .reservations-row__repeat {
    background: #ffffff;
    border: 1px solid #ed702a;
    border-radius: 4px;
    padding: 10px 14px;
    color: #ed702a;
}

/* Vehicle side */
.reservations-row__vehicle {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
}

.reservations-row__image-wrap {
    width: 83px;
    height: 42px;
    overflow: hidden;
    flex-shrink: 0;
    border-radius: 2px;
}

.reservations-row__image-wrap img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

.reservations-row__info {
    display: flex;
    flex-direction: column;
}

.reservations-row__name {
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 22px;
    color: #033239;
}

.reservations-row__subtitle {
    font-family: 'Montserrat', sans-serif;
    font-style: italic;
    font-weight: 400;
    font-size: 12px;
    line-height: 24px;
    color: #033239;
}

/* Meta side */
.reservations-row__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px 32px;
    width: 100%;
}

@media (min-width: 992px) {
    .reservations-row__meta {
        width: auto;
        flex-wrap: nowrap;
    }
}

.reservations-row__period,
.reservations-row__days {
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-size: 12px;
    color: #033239;
    white-space: nowrap;
}

.reservations-row__repeat {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 400;
    font-size: 12px;
    color: #033239;
    text-decoration: none;
    white-space: nowrap;
    border-radius: 4px;
    padding: 10px 14px;
    border: 1px solid #cdd6d7;
    margin-left: auto;
}

@media (min-width: 992px) {
    .reservations-row__repeat {
        border: none;
        padding: 10px 0;
        margin-left: 0;
    }
}

.reservations-row__repeat:hover,
.reservations-row__repeat:focus {
    color: #ed702a;
    text-decoration: none;
}

/* Footer */
.reservations-card__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-top: 8px;
}

@media (min-width: 768px) {
    .reservations-card__footer {
        flex-direction: row;
        justify-content: flex-end;
    }
}

.reservations-card__link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 16px;
    color: #033239;
    text-decoration: none;
}

.reservations-card__link:hover,
.reservations-card__link:focus {
    color: #00abc8;
    text-decoration: none;
}

.reservations-card__cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 16px;
    color: #033239;
    background: #82db39;
    border: 1px solid #82db39;
    border-radius: 4px;
    padding: 16px 24px;
    text-decoration: none;
    width: 100%;
}

@media (min-width: 768px) {
    .reservations-card__cta {
        width: auto;
    }
}

.reservations-card__cta:hover,
.reservations-card__cta:focus {
    background: #70c72e;
    border-color: #70c72e;
    color: #033239;
    text-decoration: none;
}
</style>
